import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { KycLevel, KycStepStatus, Utils, Validations, isStepDone, useKyc, useUserContext, } from '@dfx.swiss/react';
import { DfxIcon, Form, IconColor, IconVariant, SpinnerSize, StyledButton, StyledButtonColor, StyledButtonWidth, StyledInput, StyledLoadingSpinner, StyledVerticalStack, } from '@dfx.swiss/react-components';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorHint } from '../components/error-hint';
import { Layout } from '../components/layout';
import { useSettingsContext } from '../contexts/settings.context';
import { useNavigation } from '../hooks/navigation.hook';
export function LinkScreen() {
    const { translate, translateError } = useSettingsContext();
    const { getKycInfo, continueKyc, setContactData } = useKyc();
    const { user, reloadUser } = useUserContext();
    const { navigate, goBack: navigateBack } = useNavigation();
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [linkNotPossible, setLinkNotPossible] = useState(false);
    const [error, setError] = useState();
    const [contactStep, setContactStep] = useState();
    const [showLinkHint, setShowLinkHint] = useState(false);
    const kycCode = user === null || user === void 0 ? void 0 : user.kyc.hash;
    useEffect(() => {
        if (!kycCode)
            return;
        getKycInfo(kycCode)
            .then(handleInitial)
            .catch((error) => { var _a; return setError((_a = error.message) !== null && _a !== void 0 ? _a : 'Unknown error'); })
            .finally(() => setIsLoading(false));
    }, [kycCode]);
    function handleInitial(info) {
        if (info.kycLevel > 0 || !kycCode) {
            goBack();
        }
        else {
            return continueKyc(kycCode, false).then(handleReload);
        }
    }
    function handleReload(info) {
        var _a;
        if (info.kycLevel === KycLevel.Link) {
            // no account found
            linkFailed();
        }
        else if (info.kycLevel > KycLevel.Link) {
            // merged
            goBack();
        }
        else if (((_a = info.currentStep) === null || _a === void 0 ? void 0 : _a.status) === KycStepStatus.FAILED) {
            setShowLinkHint(true);
        }
        else {
            setContactStep(info.currentStep);
        }
    }
    function linkFailed() {
        setLinkNotPossible(true);
        setContactStep(undefined);
    }
    function goBack() {
        reloadUser();
        navigateBack();
    }
    const { control, handleSubmit, formState: { isValid, errors }, } = useForm({ mode: 'onTouched' });
    function onSubmit(data) {
        if (!(contactStep === null || contactStep === void 0 ? void 0 : contactStep.session) || !kycCode)
            return;
        setIsUpdating(true);
        setError(undefined);
        setContactData(kycCode, contactStep.session.url, data)
            .then((r) => (isStepDone(r) ? linkFailed() : setShowLinkHint(true)))
            .catch((error) => { var _a; return setError((_a = error.message) !== null && _a !== void 0 ? _a : 'Unknown error'); })
            .finally(() => setIsUpdating(false));
    }
    const rules = Utils.createRules({
        mail: [Validations.Required, Validations.Mail],
    });
    return (_jsx(Layout, Object.assign({ title: translate('screens/kyc', 'DFX KYC') }, { children: isLoading ? (_jsx(StyledLoadingSpinner, { size: SpinnerSize.LG })) : linkNotPossible ? (_jsxs(StyledVerticalStack, Object.assign({ gap: 6, full: true, center: true }, { children: [_jsx("p", Object.assign({ className: "text-dfxGray-700" }, { children: translate('screens/kyc', 'No matching account was found.') })), _jsx(StyledButton, { width: StyledButtonWidth.MIN, label: translate('screens/kyc', 'Complete KYC'), onClick: () => navigate('/kyc') }), _jsx(StyledButton, { width: StyledButtonWidth.MIN, label: translate('general/actions', 'Back'), onClick: goBack, color: StyledButtonColor.STURDY_WHITE })] }))) : showLinkHint ? (_jsxs(StyledVerticalStack, Object.assign({ gap: 6, full: true }, { children: [_jsx("p", Object.assign({ className: "text-dfxGray-700" }, { children: translate('screens/kyc', 'It looks like you already have an account with DFX. We have just sent you an E-Mail. Click on the sent link to confirm your mail address.') })), _jsx(StyledButton, { width: StyledButtonWidth.MIN, label: translate('general/actions', 'OK'), onClick: goBack })] }))) : (_jsx(Form, Object.assign({ control: control, rules: rules, errors: errors, onSubmit: handleSubmit(onSubmit), translate: translateError }, { children: _jsxs(StyledVerticalStack, Object.assign({ gap: 6, full: true, center: true }, { children: [_jsx(DfxIcon, { icon: IconVariant.USER_DATA, color: IconColor.BLUE }), _jsx("p", Object.assign({ className: "text-base font-bold text-dfxBlue-800" }, { children: translate('screens/kyc', 'Please enter your contact information so that we can find your account') })), _jsx(StyledInput, { name: "mail", autocomplete: "email", type: "email", label: translate('screens/kyc', 'Email address'), placeholder: translate('screens/kyc', 'example@mail.com'), full: true }), _jsx(StyledButton, { type: "submit", label: translate('general/actions', 'Next'), onClick: handleSubmit(onSubmit), width: StyledButtonWidth.FULL, disabled: !isValid, isLoading: isUpdating }), error && (_jsx("div", { children: _jsx(ErrorHint, { message: error }) }))] })) }))) })));
}
